import { useEffect, useMemo, useState } from 'react'
import { supabase } from '@/lib/supabase'
import Button from '@/components/common/Button'
import Card from '@/components/common/Card'
import { toast } from 'sonner'

const SPORT_OPTIONS = [
  { value: 'hockey', label: 'Hockey' }
]

const HOCKEY_LEVELS = [
  { value: 'boys_mite_8u', label: 'Boys Mite (8U)' },
  { value: 'boys_squirt_10u', label: 'Boys Squirt (10U)' },
  { value: 'boys_peewee_12u', label: 'Boys Peewee (12U)' },
  { value: 'boys_bantam_14u', label: 'Boys Bantam (14U)' },
  { value: 'girls_8u', label: 'Girls 8U' },
  { value: 'girls_10u', label: 'Girls 10U' },
  { value: 'girls_12u', label: 'Girls 12U' },
  { value: 'girls_15u', label: 'Girls 15U' }
]

const LEVEL_OPTIONS = {
  hockey: HOCKEY_LEVELS
}

const LEVEL_DETAIL_OPTIONS = ['','AAA','AA','A','B1','B2','B','C','Tier I','Tier II','House','Travel']

const LEVEL_ALIAS_MAP = new Map()

function sanitizeLevelKey(input = '') {
  return input.toLowerCase().replace(/[^a-z0-9]/g, '')
}

Object.entries(LEVEL_OPTIONS).forEach(([sport, levels]) => {
  levels.forEach((entry) => {
    const aliases = new Set([
      entry.value,
      entry.value.replace(/_/g, ' '),
      entry.value.replace(/_/g, ''),
      entry.label,
      entry.label.replace(/[^a-z0-9]/gi, ''),
      entry.label.replace(/\(.*\)/, '').trim()
    ])

    aliases.forEach((alias) => {
      const key = sanitizeLevelKey(alias)
      if (key) {
        LEVEL_ALIAS_MAP.set(`${sport}:${key}`, entry.value)
      }
    })
  })
})

function normalizeTeamLevelInput(level = '', sport = 'hockey') {
  const key = sanitizeLevelKey(level)
  if (!key) return null

  const direct = LEVEL_ALIAS_MAP.get(`${sport}:${key}`)
  if (direct) return direct

  return LEVEL_ALIAS_MAP.get(`hockey:${key}`) || null
}

function getLevelLabel(value = '') {
  const options = Object.values(LEVEL_OPTIONS).flat()
  return options.find((opt) => opt.value === value)?.label || value
}

export default function SeedTeamImport() {
  const [mode, setMode] = useState('manual') // 'manual' | 'csv' | 'association'
  const [importing, setImporting] = useState(false)
  
  // Manual entry form state
  const [formData, setFormData] = useState({
    name: '',
    association_name: '',
    sport_type: 'hockey',
    team_level: 'boys_squirt_10u',
    level_detail: 'B1',
    city: '',
    state: 'MN',
    home_rink_name: '',
    home_rink_alt: '',
    calendar_sync_url: '',
    calendar_sync_type: '', // Empty = auto-detect
    admin_notes: '',
    // New fields for claiming support
    data_source: 'seed_auto',
    scraped_from_url: ''
  })

  // Association import state (one URL -> many teams)
  const [associationData, setAssociationData] = useState({
    association_url: '',
    association_name: '',
    sport_type: 'hockey',
    city: '',
    state: 'MN',
    max_teams: 200,
    admin_notes: ''
  })
  const [associationPreview, setAssociationPreview] = useState(null)

  useEffect(() => {
    const options = LEVEL_OPTIONS[formData.sport_type] || []
    if (options.length > 0 && !options.some((opt) => opt.value === formData.team_level)) {
      setFormData((prev) => ({
        ...prev,
        team_level: options[0].value
      }))
    }
  }, [formData.sport_type, formData.team_level])

  const levelOptions = useMemo(() => LEVEL_OPTIONS[formData.sport_type] || [], [formData.sport_type])

  // CSV upload state
  const [csvFile, setCSVFile] = useState(null)
  const [csvPreview, setCSVPreview] = useState(null)

  async function handleManualSubmit(e) {
    e.preventDefault()
    
    try {
      setImporting(true)

      if (!formData.team_level) {
        throw new Error('Please select a team level')
      }

      const normalizedLevel = normalizeTeamLevelInput(formData.team_level, formData.sport_type)
      if (!normalizedLevel) {
        throw new Error('Unsupported team level selection')
      }

      const preparedTeam = {
        ...formData,
        name: formData.name.trim(),
        association_name: formData.association_name.trim(),
        sport_type: formData.sport_type,
        team_level: normalizedLevel,
        level_detail: formData.level_detail ? formData.level_detail.toUpperCase() : '',
        city: formData.city.trim(),
        state: formData.state.trim(),
        home_rink_name: formData.home_rink_name.trim(),
        home_rink_alt: formData.home_rink_alt.trim(),
        calendar_sync_url: formData.calendar_sync_url.trim(),
        calendar_sync_type: '',
        admin_notes: formData.admin_notes.trim(),
        // New fields for claiming support
        data_source: formData.data_source || 'seed_auto',
        scraped_from_url: formData.scraped_from_url.trim()
      }

      const { data: { session } } = await supabase.auth.getSession()
      
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/seed-teams-import`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session?.access_token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            teams: [preparedTeam]
          })
        }
      )

      const result = await response.json()

      if (!response.ok) {
        throw new Error(result.error || 'Import failed')
      }

      if (result.errors && result.errors.length > 0) {
        toast.error(`Import had errors: ${result.errors[0].error}`)
      } else if (result.inserted > 0) {
        toast.success(`Successfully imported ${formData.name}`)
        // Reset form
        setFormData({
          name: '',
          association_name: '',
          sport_type: 'hockey',
          team_level: levelOptions[0]?.value || 'boys_squirt_10u',
          level_detail: 'B1',
          city: '',
          state: 'MN',
          home_rink_name: '',
          home_rink_alt: '',
          calendar_sync_url: '',
          calendar_sync_type: '', // Empty = auto-detect
          admin_notes: '',
          data_source: 'seed_auto',
          scraped_from_url: ''
        })
      } else if (result.updated > 0) {
        toast.success(`Updated existing team ${formData.name}`)
      }
    } catch (error) {
      console.error('Error importing team:', error)
      toast.error(`Failed to import: ${error.message}`)
    } finally {
      setImporting(false)
    }
  }

  function handleCSVUpload(e) {
    const file = e.target.files?.[0]
    if (!file) return

    if (!file.name.endsWith('.csv')) {
      toast.error('Please select a CSV file')
      return
    }

    setCSVFile(file)

    // Read and preview CSV
    const reader = new FileReader()
    reader.onload = (event) => {
      const text = event.target.result
      const lines = text.split('\n').filter(l => l.trim())
      
      if (lines.length === 0) {
        toast.error('CSV file is empty')
        return
      }

      // Parse header
      const headers = lines[0].split(',').map(h => h.trim())
      
      // Parse rows (preview first 5)
      const rows = lines.slice(1, 6).map(line => {
        const values = line.split(',').map(v => v.trim())
        const row = {}
        headers.forEach((header, idx) => {
          row[header] = values[idx] || ''
        })
        return row
      })

      setCSVPreview({ headers, rows, totalRows: lines.length - 1 })
    }

    reader.readAsText(file)
  }

  async function handleCSVImport() {
    if (!csvFile) return

    setImporting(true) // Set immediately for UI feedback

    try {
      const reader = new FileReader()
      reader.onload = async (event) => {
        try {
          const text = event.target.result
          const lines = text.split('\n').filter(l => l.trim())
          
          // Parse header
          const headers = lines[0].split(',').map(h => h.trim())
          
          // Parse all rows
          const rawTeams = lines.slice(1).map((line, rowIdx) => {
            const values = line.split(',').map(v => v.trim())
            const team = {}
            headers.forEach((header, idx) => {
              team[header] = values[idx] || ''
            })
            team.__rowNumber = rowIdx + 2 // account for header row
            return team
          })

          const requiredColumns = ['name', 'association_name', 'team_level', 'city', 'state', 'calendar_sync_url']
          const normalizedTeams = []
          const rowErrors = []

          rawTeams.forEach((team) => {
            if (Object.values(team).every((value) => value === '')) {
              return // Skip empty trailing rows
            }

            const missing = requiredColumns.filter((col) => !(team[col] && team[col].trim().length > 0))
            if (missing.length > 0) {
              rowErrors.push(`row ${team.__rowNumber}: missing ${missing.join(', ')}`)
              return
            }

            const sport = (team.sport_type || 'hockey').toLowerCase()
            const normalizedLevel = normalizeTeamLevelInput(team.team_level, sport)

            if (!normalizedLevel) {
              rowErrors.push(`row ${team.__rowNumber}: invalid team_level "${team.team_level}"`)
              return
            }

            normalizedTeams.push({
              name: team.name,
              association_name: team.association_name,
              sport_type: sport,
              team_level: normalizedLevel,
              level_detail: team.level_detail ? team.level_detail.toUpperCase() : '',
              city: team.city,
              state: team.state,
              home_rink_name: team.home_rink_name || '',
              home_rink_alt: team.home_rink_alt || '',
              calendar_sync_url: team.calendar_sync_url,
              calendar_sync_type: '',
              admin_notes: team.admin_notes || '',
              // New fields for claiming support
              data_source: team.data_source || 'seed_auto',
              scraped_from_url: team.scraped_from_url || ''
            })
          })

          if (rowErrors.length > 0) {
            throw new Error(`CSV validation failed: ${rowErrors.join('; ')}`)
          }

          if (normalizedTeams.length === 0) {
            throw new Error('No valid rows found in CSV after validation')
          }

          // Import teams
          const { data: { session } } = await supabase.auth.getSession()
          
          const response = await fetch(
            `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/seed-teams-import`,
            {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${session?.access_token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({ teams: normalizedTeams })
            }
          )

          const result = await response.json()

          if (!response.ok) {
            throw new Error(result.error || 'Import failed')
          }

          // Log detailed errors
          if (result.errors && result.errors.length > 0) {
            console.error('Import errors:', result.errors)
            // Show first few errors in detail
            result.errors.slice(0, 3).forEach((err, idx) => {
              console.error(`Error ${idx + 1}:`, err.team, '-', err.error)
            })
          }

          // Show appropriate toast message
          if (result.errors.length > 0 && result.inserted === 0 && result.updated === 0) {
            toast.error(`All imports failed! Check console for details. First error: ${result.errors[0].error}`)
          } else if (result.errors.length > 0) {
            toast.warning(`Imported ${result.inserted} teams, updated ${result.updated} teams, ${result.errors.length} errors (check console)`)
          } else {
            toast.success(`Imported ${result.inserted} teams, updated ${result.updated} teams`)
          }

          // Reset
          setCSVFile(null)
          setCSVPreview(null)
        } catch (error) {
          console.error('Error importing CSV:', error)
          toast.error(`Failed to import CSV: ${error.message}`)
        } finally {
          setImporting(false) // Reset loading state
        }
      }

      reader.onerror = () => {
        toast.error('Failed to read CSV file')
        setImporting(false)
      }

      reader.readAsText(csvFile)
    } catch (error) {
      console.error('Error starting CSV import:', error)
      toast.error(`Failed to start import: ${error.message}`)
      setImporting(false)
    }
  }

  async function handleAssociationDiscover(e) {
    e?.preventDefault?.()
    try {
      setImporting(true)
      setAssociationPreview(null)

      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.access_token) {
        throw new Error('Missing session')
      }

      if (!associationData.association_url || !associationData.association_name || !associationData.city || !associationData.state) {
        throw new Error('Association URL, name, city, and state are required')
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/seed-teams-import`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            association_url: associationData.association_url.trim(),
            association_name: associationData.association_name.trim(),
            city: associationData.city.trim(),
            state: associationData.state.trim(),
            sport_type: associationData.sport_type,
            max_teams: associationData.max_teams,
            admin_notes: associationData.admin_notes,
            dry_run: true
          })
        }
      )

      const result = await response.json()
      if (!response.ok) {
        throw new Error(result.error || 'Discovery failed')
      }

      setAssociationPreview(result.teams || [])
      toast.success(`Discovered ${result.discovered_count || (result.teams || []).length} teams`)
    } catch (error) {
      console.error('Association discovery error:', error)
      toast.error(`Discovery failed: ${error.message}`)
    } finally {
      setImporting(false)
    }
  }

  async function handleAssociationImport() {
    try {
      setImporting(true)

      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.access_token) {
        throw new Error('Missing session')
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/seed-teams-import`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            association_url: associationData.association_url.trim(),
            association_name: associationData.association_name.trim(),
            city: associationData.city.trim(),
            state: associationData.state.trim(),
            sport_type: associationData.sport_type,
            max_teams: associationData.max_teams,
            admin_notes: associationData.admin_notes,
            dry_run: false
          })
        }
      )

      const result = await response.json()
      if (!response.ok) {
        throw new Error(result.error || 'Import failed')
      }

      if (result.errors && result.errors.length > 0) {
        toast.warning(`Imported ${result.inserted} teams, updated ${result.updated} teams, ${result.errors.length} errors (check console)`)
        console.error('Association import errors:', result.errors)
      } else {
        toast.success(`Imported ${result.inserted} teams, updated ${result.updated} teams`)
      }
    } catch (error) {
      console.error('Association import error:', error)
      toast.error(`Import failed: ${error.message}`)
    } finally {
      setImporting(false)
    }
  }

  function downloadTemplate() {
    const template = `name,association_name,sport_type,team_level,level_detail,city,state,home_rink_name,home_rink_alt,calendar_sync_url,admin_notes,data_source,scraped_from_url
CCHA Squirt B1 Gold,Chaska Chanhassen Hockey Association,hockey,boys_squirt_10u,B1,Chaska,MN,Chaska Community Center,Victoria Community Center,https://www.cchockey.org/calendar/ical/194514,,seed_auto,https://www.cchockey.org/teams/squirts
Wayzata Squirt B1 Gold,Wayzata Youth Hockey Association,hockey,boys_squirt_10u,B1,Wayzata,MN,Plymouth Ice Center,,https://www.wayzatahockey.org/team/141011/schedule,,seed_auto,https://www.wayzatahockey.org/teams
Prior Lake-Savage Squirt B1 Gold,Prior Lake-Savage Hockey Association,hockey,boys_squirt_10u,B1,Prior Lake,MN,Dakotah Ice Center,,webcal://www.plsha.com/ical_feed?tags=5220338,,seed_auto,`

    const blob = new Blob([template], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'seed-teams-template.csv'
    document.body.appendChild(a) // Append to body for better browser compatibility
    a.click()
    document.body.removeChild(a) // Clean up
    URL.revokeObjectURL(url)
  }

  return (
    <div className="space-y-6">
      {/* Mode Selector */}
      <Card>
        <div className="p-6">
          <div className="flex gap-4 mb-6">
            <button
              onClick={() => setMode('manual')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                mode === 'manual'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Manual Entry
            </button>
            <button
              onClick={() => setMode('csv')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                mode === 'csv'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              CSV Upload
            </button>
            <button
              onClick={() => setMode('association')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                mode === 'association'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Association URL
            </button>
          </div>

          {mode === 'manual' ? (
            /* Manual Entry Form */
            <form onSubmit={handleManualSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Team Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Association *
                  </label>
                  <input
                    type="text"
                    value={formData.association_name}
                    onChange={(e) => setFormData({ ...formData, association_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., CCHA, Wayzata"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Sport *
                  </label>
                  <select
                    value={formData.sport_type}
                    onChange={(e) => setFormData({ ...formData, sport_type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    {SPORT_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Team Level *
                  </label>
                  <select
                    value={formData.team_level}
                    onChange={(e) => setFormData({ ...formData, team_level: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    {levelOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tier / Detail
                  </label>
                  <select
                    value={formData.level_detail}
                    onChange={(e) => setFormData({ ...formData, level_detail: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    {LEVEL_DETAIL_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option === '' ? 'Select (optional)' : option}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    City *
                  </label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Chaska"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    State *
                  </label>
                  <input
                    type="text"
                    value={formData.state}
                    onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="MN"
                    required
                  />
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Home Rink
                  </label>
                  <input
                    type="text"
                    value={formData.home_rink_name}
                    onChange={(e) => setFormData({ ...formData, home_rink_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Chaska Community Center"
                  />
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Secondary Rink
                  </label>
                  <input
                    type="text"
                    value={formData.home_rink_alt}
                    onChange={(e) => setFormData({ ...formData, home_rink_alt: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Optional: e.g., Victoria Community Center"
                  />
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Calendar URL *
                  </label>
                  <input
                    type="url"
                    value={formData.calendar_sync_url}
                    onChange={(e) => setFormData({ ...formData, calendar_sync_url: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="https://..."
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Calendar Type (Optional)
                  </label>
                  <select
                    value={formData.calendar_sync_type}
                    onChange={(e) => setFormData({ ...formData, calendar_sync_type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Auto-detect (recommended)</option>
                    <option value="ical">iCal/RSS (.ics feed)</option>
                    <option value="webcal">WebCal (webcal:// URLs)</option>
                    <option value="html">HTML (web schedule page)</option>
                  </select>
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Admin Notes
                  </label>
                  <textarea
                    value={formData.admin_notes}
                    onChange={(e) => setFormData({ ...formData, admin_notes: e.target.value })}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Internal notes..."
                  />
                </div>
              </div>

              <div className="flex justify-end">
                <Button
                  type="submit"
                  variant="primary"
                  disabled={importing}
                >
                  {importing ? 'Importing...' : 'Import Team'}
                </Button>
              </div>
            </form>
          ) : mode === 'csv' ? (
            /* CSV Upload */
            <div className="space-y-4">
              <div>
                <Button
                  variant="ghost"
                  onClick={downloadTemplate}
                  className="mb-4"
                >
                  📥 Download CSV Template
                </Button>
              </div>

              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleCSVUpload}
                  className="hidden"
                  id="csv-upload"
                />
                <label
                  htmlFor="csv-upload"
                  className="cursor-pointer"
                >
                  <div className="text-gray-600 mb-2">
                    📄 Click to upload CSV file
                  </div>
                  <div className="text-sm text-gray-500">
                    Required columns: name, association_name, sport_type, team_level, city, state, calendar_sync_url
                  </div>
                </label>
              </div>

              {csvPreview && (
                <div className="mt-4">
                  <h3 className="font-medium text-gray-900 mb-2">
                    Preview ({csvPreview.totalRows} teams)
                  </h3>
                  <div className="border border-gray-300 rounded-lg overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          {csvPreview.headers.map(header => (
                            <th key={header} className="px-4 py-2 text-left font-medium text-gray-700">
                              {header}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {csvPreview.rows.map((row, idx) => (
                          <tr key={idx} className="border-t border-gray-200">
                            {csvPreview.headers.map(header => (
                              <td key={header} className="px-4 py-2 text-gray-600">
                                {row[header]}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {importing && (
                    <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="animate-spin h-5 w-5 border-2 border-blue-600 border-t-transparent rounded-full"></div>
                        <div className="text-sm text-blue-900">
                          <div className="font-medium">Importing {csvPreview.totalRows} teams...</div>
                          <div className="text-blue-700 mt-1">
                            Geocoding locations and validating calendars. This may take 10-20 seconds.
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="mt-4 flex justify-end gap-3">
                    <Button
                      variant="ghost"
                      onClick={() => {
                        setCSVFile(null)
                        setCSVPreview(null)
                      }}
                      disabled={importing}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="primary"
                      onClick={handleCSVImport}
                      disabled={importing}
                    >
                      {importing ? 'Importing...' : `Import ${csvPreview.totalRows} Teams`}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            /* Association Import */
            <form onSubmit={handleAssociationDiscover} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Association Homepage URL *
                  </label>
                  <input
                    type="url"
                    value={associationData.association_url}
                    onChange={(e) => setAssociationData({ ...associationData, association_url: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="https://www.cchockey.org"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    We’ll scan this site for team pages like <code>/team/12345</code>.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Association Name *
                  </label>
                  <input
                    type="text"
                    value={associationData.association_name}
                    onChange={(e) => setAssociationData({ ...associationData, association_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., CCHA"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Sport
                  </label>
                  <select
                    value={associationData.sport_type}
                    onChange={(e) => setAssociationData({ ...associationData, sport_type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    {SPORT_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Default City *
                  </label>
                  <input
                    type="text"
                    value={associationData.city}
                    onChange={(e) => setAssociationData({ ...associationData, city: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Chaska"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Default State *
                  </label>
                  <input
                    type="text"
                    value={associationData.state}
                    onChange={(e) => setAssociationData({ ...associationData, state: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="MN"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Max Teams
                  </label>
                  <input
                    type="number"
                    value={associationData.max_teams}
                    onChange={(e) => setAssociationData({ ...associationData, max_teams: parseInt(e.target.value || '0', 10) || 200 })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    min={1}
                    max={500}
                  />
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Admin Notes
                  </label>
                  <textarea
                    value={associationData.admin_notes}
                    onChange={(e) => setAssociationData({ ...associationData, admin_notes: e.target.value })}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Optional internal notes..."
                  />
                </div>
              </div>

              <div className="flex items-center justify-between gap-3">
                <Button
                  type="submit"
                  variant="primary"
                  disabled={importing}
                >
                  {importing ? 'Discovering...' : 'Discover Teams'}
                </Button>

                {Array.isArray(associationPreview) && associationPreview.length > 0 && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleAssociationImport}
                    disabled={importing}
                  >
                    {importing ? 'Importing...' : `Import ${associationPreview.length} Teams`}
                  </Button>
                )}
              </div>

              {Array.isArray(associationPreview) && associationPreview.length > 0 && (
                <div className="mt-4">
                  <h3 className="font-medium text-gray-900 mb-2">
                    Preview ({associationPreview.length} teams)
                  </h3>
                  <div className="border border-gray-300 rounded-lg overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left font-medium text-gray-700">Name</th>
                          <th className="px-4 py-2 text-left font-medium text-gray-700">Level</th>
                          <th className="px-4 py-2 text-left font-medium text-gray-700">Calendar URL</th>
                        </tr>
                      </thead>
                      <tbody>
                        {associationPreview.slice(0, 25).map((t, idx) => (
                          <tr key={idx} className="border-t border-gray-200">
                            <td className="px-4 py-2 text-gray-700">{t.name}</td>
                            <td className="px-4 py-2 text-gray-600">
                              {t.team_level}{t.level_detail ? ` ${t.level_detail}` : ''}
                            </td>
                            <td className="px-4 py-2 text-gray-600">
                              <span className="font-mono text-xs">{t.calendar_sync_url}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {associationPreview.length > 25 && (
                    <p className="text-xs text-gray-500 mt-2">
                      Showing first 25 teams.
                    </p>
                  )}
                </div>
              )}
            </form>
          )}
        </div>
      </Card>

      {/* Help Text */}
      <Card>
        <div className="p-6">
          <h3 className="font-semibold text-gray-900 mb-2">Import Instructions</h3>
          <ul className="text-sm text-gray-600 space-y-1">
            <li>• Teams will be geocoded automatically based on city/state</li>
            <li>• Calendar sync will be enabled by default</li>
            <li>• If a team with the same name + association exists, it will be updated</li>
            <li>• Use the dropdown values for team level (e.g., “Boys Squirt (10U)”, “Girls 12U”)</li>
            <li>• Tier/Detail supports: AAA, AA, A, B1, B2, C, House, Travel, etc.</li>
            <li>• Secondary rink is optional (for associations that split ice across venues)</li>
            <li><strong>• Calendar Type Auto-Detection:</strong></li>
            <li>&nbsp;&nbsp;- <strong>iCal/RSS</strong>: URLs with .ics, /ical, or _rss_feed → Auto-detected</li>
            <li>&nbsp;&nbsp;- <strong>WebCal</strong>: URLs starting with webcal:// → Auto-detected & converted to https://</li>
            <li>&nbsp;&nbsp;- <strong>HTML</strong>: Schedule/calendar page URLs (Crossbar, etc.) → Auto-detected</li>
            <li>&nbsp;&nbsp;- Leave <code>calendar_sync_type</code> blank to use auto-detection (recommended)</li>
            <li>&nbsp;&nbsp;- Or specify explicitly: ical, webcal, or html</li>
            <li><strong>• New Claiming Fields:</strong></li>
            <li>&nbsp;&nbsp;- <strong>data_source</strong>: "seed_auto" for scraped teams, "user_managed" for user-created</li>
            <li>&nbsp;&nbsp;- <strong>scraped_from_url</strong>: Original URL where team data was discovered (for claim verification)</li>
          </ul>
        </div>
      </Card>
    </div>
  )
}

